"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackArticles = [{
  id: "fallback-1",
  title: "Kế hoạch tổ chức tọa đàm khoa học về biến đổi khí hậu và địa lý môi trường năm 2025",
  excerpt: "Thông báo chính thức về việc triển khai chương trình sinh hoạt học thuật và khảo sát thực địa chuyên ngành của các hội viên trên toàn quốc.",
  date: "15/03/2025",
  category: "Thông báo học thuật",
  link: "/NewsAndEventsPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/environmental_geography_conference.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/environmental_geography_conference.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/environmental_geography_conference.png-wvc-sizes",
    alt: "Hội thảo chuyên đề địa lý môi trường"
  }
}, {
  id: "fallback-2",
  title: "Chương trình nghiên cứu thực địa và đánh giá suy thoái tài nguyên đất vùng Tây Bắc",
  excerpt: "Nhiệm vụ phối hợp liên ngành nhằm lập bản đồ hiện trạng môi trường và đề xuất giải pháp bảo vệ lưu vực sông bền vững.",
  date: "28/02/2025",
  category: "Nghiên cứu ứng dụng",
  link: "/NewsAndEventsPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/soil_water_field_survey.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/soil_water_field_survey.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/soil_water_field_survey.png-wvc-sizes",
    alt: "Khảo sát thực địa tài nguyên lưu vực sông"
  }
}, {
  id: "fallback-3",
  title: "Công tác chuẩn bị hội nghị thường niên và kiện toàn công tác hội viên Hội VAEG",
  excerpt: "Báo cáo tiến độ các nội dung trọng tâm theo tinh thần Điều lệ Hội được Bộ Nội vụ phê duyệt tại Quyết định số 1157/QĐ-BNV.",
  date: "10/02/2025",
  category: "Công tác Hội",
  link: "/NewsAndEventsPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/vaeg_annual_assembly.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/vaeg_annual_assembly.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/vaeg_annual_assembly.png-wvc-sizes",
    alt: "Hội nghị thường niên Hội Địa lý môi trường Việt Nam"
  }
}];
function NewsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="flex flex-col overflow-hidden rounded-sm border border-border bg-card shadow-xs animate-pulse">
            <div className="h-48 w-full bg-muted" />
            <div className="flex flex-1 flex-col p-6 space-y-4">
              <div className="h-4 w-24 rounded bg-muted" />
              <div className="h-6 w-5/6 rounded bg-muted" />
              <div className="space-y-2">
                <div className="h-3.5 w-full rounded bg-muted" />
                <div className="h-3.5 w-4/5 rounded bg-muted" />
              </div>
              <div className="pt-4 mt-auto border-t border-border">
                <div className="h-4 w-28 rounded bg-muted" />
              </div>
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-sm border border-border bg-muted/40 p-6 text-center">
        <p className="text-sm text-muted-foreground mb-3">
          Không thể tải dữ liệu tin tức từ cổng thông tin điện tử.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Tải lại dữ liệu
        </Button>
      </div>;
  }
  const displayItems = posts && posts.length > 0 ? posts.slice(0, 3) : null;
  if (displayItems && displayItems.length > 0) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {displayItems.map((post, i) => {
        const postDate = post.date ? new Date(post.date).toLocaleDateString("vi-VN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        }) : "Chưa công bố";
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : "Tin tức hoạt động";
        const articleUrl = post.link || "/NewsAndEventsPage";
        return <Card key={post.id} data-index={i} className="group flex flex-col justify-between overflow-hidden rounded-sm border border-border bg-card p-0 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-secondary hover:shadow-md">
              <div>
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title || "Tin tức VAEG"} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-accent/40 text-muted-foreground">
                      <FileText className="size-12 stroke-[1.5] text-primary/40" />
                    </div>}
                  <div className="absolute top-3 left-3">
                    <Badge variant="secondary" className="rounded-sm bg-card/95 px-2.5 py-1 text-xs font-medium text-foreground backdrop-blur-xs shadow-xs border border-border/80">
                      {categoryName}
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-6">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                    <SafeHydrate><Calendar className="size-3.5 stroke-[1.75]" /></SafeHydrate>
                    <span>{postDate}</span>
                  </div>

                  <h3 className="line-clamp-2 text-lg font-semibold text-foreground leading-snug group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>

                  {post.excerpt && <p className="mt-3 line-clamp-3 text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{
                __html: post.excerpt.replace(/<[^>]*>?/gm, "")
              }} />}
                </CardContent>
              </div>

              <div className="px-6 pb-6 pt-0 mt-auto">
                <Link to={articleUrl} className="inline-flex items-center gap-1.5 text-sm font-semibold text-secondary hover:text-secondary/80 transition-colors">
                  <span>Xem văn bản chi tiết</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </div>
            </Card>;
      })}
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {fallbackArticles.map((article, i) => <Card key={article.id} data-index={i} className="group flex flex-col justify-between overflow-hidden rounded-sm border border-border bg-card p-0 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-secondary hover:shadow-md">
          <div>
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
              <img src={article.image.src} data-wvc-srcset={article.image.srcset} data-wvc-sizes={article.image.sizes} alt={article.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute top-3 left-3">
                <Badge variant="secondary" className="rounded-sm bg-card/95 px-2.5 py-1 text-xs font-medium text-foreground backdrop-blur-xs shadow-xs border border-border/80">
                  {article.category}
                </Badge>
              </div>
            </div>

            <CardContent className="p-6">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                <SafeHydrate><Calendar className="size-3.5 stroke-[1.75]" /></SafeHydrate>
                <span>{article.date}</span>
              </div>

              <h3 className="line-clamp-2 text-lg font-semibold text-foreground leading-snug group-hover:text-primary transition-colors">
                {article.title}
              </h3>

              <p className="mt-3 line-clamp-3 text-sm text-muted-foreground leading-relaxed">
                {article.excerpt}
              </p>
            </CardContent>
          </div>

          <div className="px-6 pb-6 pt-0 mt-auto">
            <Link to={article.link} className="inline-flex items-center gap-1.5 text-sm font-semibold text-secondary hover:text-secondary/80 transition-colors">
              <span>Xem chi tiết thông báo</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </Card>)}
    </div>;
}
export default function News() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="tin-tức-sự-kiện-mới-nhất" data-nav="light" className="bg-background py-16 md:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 md:mb-16">
          <div className="flex flex-col items-start gap-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Thông tin điều hành & sinh hoạt học thuật
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-secondary tracking-tight">
              Tin tức & sự kiện mới nhất
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <div className="h-1 w-12 rounded-xs bg-secondary" />
              <div className="h-px w-24 bg-border" />
            </div>
            <p className="mt-2 max-w-2xl text-base text-muted-foreground leading-relaxed">
              Cập nhật các văn bản chỉ đạo, thông cáo hội thảo, chương trình nghiên cứu thực địa và sinh hoạt khoa học định kỳ của Hội Địa lý môi trường Việt Nam.
            </p>
          </div>
        </div>

        <PostsProvider className="w-full" wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <NewsGridConsumer />
        </PostsProvider>

        <div className="mt-12 md:mt-16 flex justify-center">
          <Button asChild variant="outline" className="rounded-sm border-primary px-7 py-3 text-sm font-semibold text-primary hover:bg-accent hover:text-accent-foreground transition-[color,background-color,border-color] h-auto">
            <Link to="/NewsAndEventsPage" className="inline-flex items-center gap-2">
              <span>Xem tất cả tin tức</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}